import { useState, type FormEvent } from "react";
import { ApiError, loginStep1, loginStep2 } from "@/api/client";
interface LoginProps {
  onSuccess: () => void;
}
type Step = "credentials" | "totp";
export default function Login({ onSuccess }: LoginProps) {
  const [step, setStep] = useState<Step>("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  async function handleCredentialsSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginStep1(username, password);
      if (!result.twoFactorRequired) {
        onSuccess();
        return;
      }
      setPendingToken(result.pendingToken);
      setStep("totp");
    } catch (submitError) {
      setError(submitError instanceof ApiError ? submitError.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }
  async function handleTotpSubmit(event: FormEvent) {
    event.preventDefault();
    if (!pendingToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await loginStep2(pendingToken, totpCode);
      onSuccess();
    } catch (submitError) {
      setError(submitError instanceof ApiError ? submitError.message : "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="font-mono text-lg font-semibold tracking-wide text-ink">
            <span className="text-turquoise">AI</span> Trader
          </div>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-ink-faint">Console access</div>
        </div>

        <div className="rounded-xl border border-line bg-panel p-6 shadow-panel">
          {step === "credentials" ? (
            <form className="space-y-4" onSubmit={handleCredentialsSubmit}>
              <div>
                <label htmlFor="username" className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className="mt-1.5 w-full rounded-md border border-line-soft bg-raised px-3 py-2 font-mono text-sm text-ink outline-none focus:border-turquoise"
                />
              </div>
              <div>
                <label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1.5 w-full rounded-md border border-line-soft bg-raised px-3 py-2 font-mono text-sm text-ink outline-none focus:border-turquoise"
                />
              </div>
              {error && <p className="font-mono text-xs text-sell">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-turquoise/90 py-2 font-mono text-sm font-semibold text-void transition hover:bg-turquoise disabled:opacity-50"
              >
                {submitting ? "Checking..." : "Continue"}
              </button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleTotpSubmit}>
              <div>
                <label htmlFor="totp" className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">
                  6-digit authenticator code
                </label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 w-full rounded-md border border-line-soft bg-raised px-3 py-2 text-center font-mono text-lg tracking-[0.4em] text-ink outline-none focus:border-turquoise"
                />
              </div>
              {error && <p className="font-mono text-xs text-sell">{error}</p>}
              <button
                type="submit"
                disabled={submitting || totpCode.length !== 6}
                className="w-full rounded-md bg-turquoise/90 py-2 font-mono text-sm font-semibold text-void transition hover:bg-turquoise disabled:opacity-50"
              >
                {submitting ? "Verifying..." : "Verify"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("credentials");
                  setTotpCode("");
                  setError(null);
                }}
                className="w-full font-mono text-[11px] text-ink-faint hover:text-ink-dim"
              >
                Back
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center font-mono text-[11px] text-ink-faint">
          Trading tool for monitoring only. Not financial advice.
        </p>
      </div>
    </div>
  );
}
